import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { vehicles } from '../data/vehicles';

const { FiBell, FiTrendingDown, FiTrendingUp, FiClock, FiDollarSign, FiCheck, FiZap } = FiIcons;

const PricingAlerts = ({ targetModel }) => {
  const [activeAlerts, setActiveAlerts] = useState([]);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [email, setEmail] = useState('');
  const [marketTrend, setMarketTrend] = useState('falling');

  useEffect(() => {
    // Generate simulated active alerts
    const alerts = [
      {
        id: 1,
        model: 'Q5',
        drop: 1200,
        reason: 'Holiday Special',
        expires: '24h'
      },
      {
        id: 2,
        model: 'A4',
        drop: 850,
        reason: 'Inventory Clearance',
        expires: '3d'
      }
    ];
    setActiveAlerts(alerts);
  }, []);

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (!email) return;
    setIsSubscribed(true);
    // Simulate API call
    setTimeout(() => {
      alert(`Pricing alerts activated for ${targetModel || 'all models'}! You will receive notifications at ${email}.`);
    }, 500);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden shadow-2xl"
    >
      {/* Header & Market Trend */}
      <div className="p-5 border-b border-gray-700 bg-gradient-to-r from-gray-800 to-gray-900">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-xl font-bold text-white flex items-center">
              <SafeIcon icon={FiBell} className="w-5 h-5 mr-2 text-audi-red" />
              Dynamic Pricing Alerts
            </h3>
            <p className="text-sm text-gray-400">Real-time market tracking & value alerts</p>
          </div>
          <div className="flex flex-col items-end">
            <span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">Market Status</span>
            <div className={`flex items-center space-x-2 px-3 py-1 rounded-full text-xs font-bold ${
              marketTrend === 'falling' ? 'bg-green-900/30 text-green-400 border border-green-800' : 'bg-red-900/30 text-red-400 border border-red-800'
            }`}>
              <SafeIcon icon={marketTrend === 'falling' ? FiTrendingDown : FiTrendingUp} className="w-3 h-3" />
              <span>{marketTrend === 'falling' ? 'BUYER\'S MARKET' : 'HIGH DEMAND'}</span>
            </div>
          </div>
        </div>

        {/* Live Alerts Feed */}
        <div className="space-y-3">
          {activeAlerts.map(alert => (
            <motion.div
              key={alert.id}
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              className="bg-gray-900/50 border border-gray-700 rounded-lg p-3 flex items-center justify-between"
            >
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-audi-red/10 rounded-full flex items-center justify-center">
                  <SafeIcon icon={FiZap} className="w-5 h-5 text-audi-red" />
                </div>
                <div>
                  <div className="text-sm font-bold text-white">Price Drop: Audi {alert.model}</div>
                  <div className="text-xs text-gray-400">{alert.reason}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-green-400 font-bold">-${alert.drop}</div>
                <div className="text-[10px] text-gray-500 flex items-center justify-end">
                  <SafeIcon icon={FiClock} className="w-2 h-2 mr-1" />
                  Expires in {alert.expires}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Subscription Section */}
      <div className="p-5 bg-gray-900/30">
        {!isSubscribed ? (
          <form onSubmit={handleSubscribe} className="space-y-4">
            <div className="text-center mb-4">
              <p className="text-sm text-gray-300">
                Don't miss the next price drop. Subscribe to alerts for {targetModel ? `the Audi ${targetModel}` : 'our inventory'}.
              </p>
            </div>
            <div className="flex space-x-2">
              <div className="relative flex-1">
                <SafeIcon icon={FiIcons.FiMail} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 w-4 h-4" />
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-10 pr-4 py-2 text-sm text-white focus:ring-2 focus:ring-audi-red outline-none"
                  required
                />
              </div>
              <button
                type="submit"
                className="bg-audi-red hover:bg-red-700 text-white px-6 py-2 rounded-lg text-sm font-bold transition-all flex items-center space-x-2"
              >
                <span>Notify Me</span>
              </button>
            </div>
          </form>
        ) : (
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="text-center py-2"
          >
            <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-2">
              <SafeIcon icon={FiCheck} className="w-5 h-5 text-white" />
            </div>
            <p className="text-white font-bold text-sm">Alerts Activated!</p>
            <p className="text-xs text-gray-400">We'll email you the moment prices change.</p>
          </motion.div>
        )}
      </div>

      {/* Value Guarantee Footer */}
      <div className="p-4 bg-gray-800 border-t border-gray-700 flex items-center justify-center space-x-6">
        <div className="flex items-center space-x-2">
          <SafeIcon icon={FiDollarSign} className="w-4 h-4 text-audi-red" />
          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">Price Protection</span>
        </div>
        <div className="flex items-center space-x-2">
          <SafeIcon icon={FiCheck} className="w-4 h-4 text-audi-red" />
          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">Verified Savings</span>
        </div>
      </div>
    </motion.div>
  );
};

export default PricingAlerts;